import { useMutation, useQueryClient, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query';
import { createSKUConfig, getAllSKUConfigs, getSKUConfigById, getSKUConfigByName, checkMatchingSKUConfigs, updateSKUConfig } from '../api/sku';
import { SKUConfigKey, SKUConfigNameUniqueKey, MatchingSKUConfigsKey } from '../configs/sku';
import { SKUConfigListResponse, SKUDataType, SKUNameUniqueCheckType } from '../@types/sku.type';
import { QueryOption } from '../@types';

export const useGetSKUConfigs = (queryOption: QueryOption): UseQueryResult<SKUConfigListResponse> => {
  return useQuery({
    queryKey: [SKUConfigKey, queryOption.page, queryOption.pageSize, queryOption.filterModel],
    queryFn: () => getAllSKUConfigs(queryOption)
  });
};

export const useGetSKUConfigById = (id: number): UseQueryResult<SKUDataType> => {
  return useQuery({
    queryKey: [SKUConfigKey, id],
    queryFn: () => id && getSKUConfigById(id)
  });
};

export const useCreateSKUConfig = (): UseMutationResult => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: any) => {
      const queryResult = await createSKUConfig(data);
      return queryResult;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SKUConfigKey]
      });
    }
  });
};

export const useUpdateSKUConfig = (): UseMutationResult => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => updateSKUConfig(data.id, data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SKUConfigKey]
      });
    }
  }); 
};

export const useGetSKUConfigByName = (data: any): UseQueryResult<SKUNameUniqueCheckType> => {
  return useQuery({
    queryKey: [SKUConfigNameUniqueKey, data.name, data.id],
    queryFn: () => getSKUConfigByName(data.name, data.id),
    enabled: false
  });
};

export const useCheckMatchingSKUConfigs = (id: number, inPartId: number, outPartId: number): UseQueryResult<boolean> => {
  return useQuery({
    queryKey: [MatchingSKUConfigsKey, id, inPartId, outPartId],
    queryFn: () => inPartId && outPartId && checkMatchingSKUConfigs(inPartId, outPartId)
  });
};